import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useScrollHighlight } from "@/hooks/use-scroll-highlight";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format, subDays } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  AlertTriangle, 
  TrendingDown, 
  Flame, 
  TrendingUp, 
  AlertCircle,
  Eye,
  PawPrint,
  MapPin,
  X,
  Filter,
  Info,
  ExternalLink,
  LayoutGrid,
  Activity,
  RefreshCw,
  Loader2,
  Kanban,
  ArrowUpDown,
  ArrowLeft,
  Bell,
  List,
  ClipboardCheck,
  HelpCircle,
  CheckCircle2,
  BarChart3,
  Award,
  ChevronUp,
  ChevronDown,
  Tag,
  FileText,
  Flag,
  Heart,
  Clock,
  CheckCheck,
  SlidersHorizontal,
} from "lucide-react";
import { AnimalCell } from "@/components/animals/animal-cell";
import { CollapsibleFilterHeader } from "@/components/ui/collapsible-filter-header";
import { ExpandCollapseButton } from "@/components/ui/expand-collapse-button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Link, useSearch, useLocation } from "wouter";
import { PageHeader } from "@/components/page-header";
import { SearchEmptyState } from "@/components/ui/search-empty-state";
import { InsightsPageWrapper } from "@/components/insights/insights-layout";
import { useInsightsContext } from "@/contexts/insights-context";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { isWorkflowDashboardEnabled } from "@/lib/feature-flags";
import { WorkflowDashboardGrid, WorkflowBucketDetail, WorkflowBoardView, WorkflowSearch } from "@/components/behavior/workflow-dashboard";
import { WorkflowEvalSummary, workflowSummaryFilterLabel, matchesWorkflowSummaryFilter } from "@/components/behavior/workflow-dashboard/WorkflowEvalSummary";
import type { WorkflowSummaryTileFilter } from "@/components/behavior/workflow-dashboard/WorkflowEvalSummary";
import { AnimalDetailDrawer } from "@/components/animals/AnimalDetailDrawer";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { fetchWorkflowDashboard } from "@/lib/api/behavior-workflow";
import { ReportActionItems } from "@/components/report-action-items";
import type { WorkflowDashboardResponse, WorkflowDashboardItem, BucketKey, Track, EvalStatus } from "@/lib/api/workflow-types";
import { VALID_TRACKS, VALID_EVAL_STATUSES, buildBucketKey, TRACK_LABELS, EVAL_STATUS_LABELS } from "@/lib/api/workflow-types";
import { EvalStatusBadge } from "@/components/animals/EvalStatusBadge";
import { BehaviorAnalytics } from "@/components/behavior-analytics";
import { HintBlock } from "@/components/ui/hint-block";
import { HintsToggle } from "@/components/ui/hints-toggle";
import { usePageTitle } from "@/hooks/use-page-title";
import { useReportFreshness } from "@/hooks/use-report-freshness";
import { useZoneLabels } from "@/hooks/use-zone-labels";
import { useFollowedAnimalIds } from "@/hooks/use-followed-animals";
import { ReportFreshnessModal } from "@/components/report-freshness-modal";

const BEHAVIOR_HINTS_STORAGE_KEY = "beti-behavior-show-hints";

type WorkflowViewMode = 'grid' | 'board' | 'list';

// Gate I8.15.1: List view sortable columns
type ListSortColumn = 'animal' | 'track' | 'evalStatus' | 'adoptionLevel' | 'location' | 'intakeDate' | 'onHold' | 'attention' | 'evalChecklist' | null;
type ListSortDirection = 'asc' | 'desc';

interface AnimalAlerts {
  struggling: boolean;
  worsening: boolean;
  streak: boolean;
  spike: boolean;
  highSeverity: boolean;
  flagged: boolean;
  // Gate R4: flag signals
  hasLongNoteFlag: boolean;
  hasKeywordFlag: boolean;
  hasHighlightFlag: boolean;
  overdueForInteraction: boolean;
}

interface AnimalAlertDetails {
  strugglingNowCount: number;
  trend3dLabel: string | null;
  recentHighSeverityCount: number;
  streakDays: number;
  dailySpikeMultiplier: number | null;
}

interface FlaggedAnimal {
  animalId: string;
  name: string;
  species: string;
  photoUrl: string | null;
  location: string;
  zoneCode: string | null;
  computedAt: string;
  alerts: AnimalAlerts;
  details: AnimalAlertDetails;
  lastConcern: {
    date: string;
    scoreCode: string | null;
  } | null;
}

interface BehaviorAlertsSummary {
  total: number;
  flagged: number;
  struggling: number;
  worsening: number;
  streak: number;
  spike: number;
  highSeverity: number;
  moderateHighConcern?: {
    interactions: number;
    animals: number;
  };
  // Gate R4: flag signal counts
  hasLongNoteFlag?: number;
  hasKeywordFlag?: number;
  hasHighlightFlag?: number;
  overdueForInteraction?: number;
}

interface BehaviorAlertsResponse {
  summary: BehaviorAlertsSummary;
  animals: FlaggedAnimal[];
  appliedFilters: {
    alertType: string | null;
    severity: string | null;
    location: string | null;
    species: string | null;
  };
}

interface AlertThresholds {
  strugglingNowThreshold: number;
  strugglingNowHours: number;
  streakThresholdDays: number;
  spikeMultiplier: number;
  trend3dThreshold: number;
  recentHighSeverityDays: number;
  recentHighSeverityFlagThreshold: number;
}

type AlertType = null | "any" | "struggling" | "worsening" | "streak" | "spike" | "highSeverity" | "hasLongNoteFlag" | "hasKeywordFlag" | "hasHighlightFlag" | "overdueForInteraction";

function WorkflowStaleAlert({ workflowStaleMsgLocations, workflowLocationFilter }: { 
  workflowStaleMsgLocations: { locationCode: string; locationLabel: string }[];
  workflowLocationFilter: string;
}) {
  const [dismissed, setDismissed] = useState(false);
  
  if (workflowStaleMsgLocations.length === 0 || dismissed) return null;
  
  return (
    <div data-testid="workflow-missing-report-alert">
      <Alert 
        className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-500 relative" 
        data-testid="workflow-stale-alert"
      >
        <Info className="h-4 w-4" />
        <button 
          type="button"
          className="absolute top-3 right-3 p-1 rounded-sm text-blue-600 hover:text-blue-800 hover:bg-blue-200 dark:text-blue-400 dark:hover:text-blue-200 dark:hover:bg-blue-800 transition-colors"
          onClick={() => setDismissed(true)}
          data-testid="button-dismiss-workflow-alert"
        >
          <X className="h-4 w-4" />
        </button>
        <AlertTitle className="text-blue-800 dark:text-blue-200 pr-8">
          {workflowLocationFilter !== 'all' ? (
            <>Missing today's report for {workflowStaleMsgLocations[0].locationLabel}. Kennel data may be incomplete.</>
          ) : (
            <>
              Missing today's report for:{" "}
              {workflowStaleMsgLocations.slice(0, 3).map(loc => loc.locationLabel).join(", ")}
              {workflowStaleMsgLocations.length > 3 && ` +${workflowStaleMsgLocations.length - 3} more`}
            </>
          )}
        </AlertTitle>
        <AlertDescription className="text-blue-700 dark:text-blue-300 flex flex-wrap items-center justify-between gap-2">
          <span>Upload a new All Pets report to see the latest kennel data.</span>
          <Button variant="outline" size="sm" asChild className="h-7 bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800 ml-auto">
            <a href="/reports" target="_blank" rel="noopener noreferrer" data-testid="workflow-alert-link-reports">
              View Reports
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}

function InteractionDataWarning({ staleLocationCount, onOpenModal }: { staleLocationCount?: number; onOpenModal?: () => void }) {
  const [dismissed, setDismissed] = useState(false);
  const hasStaleData = staleLocationCount && staleLocationCount > 0;
  
  if (!hasStaleData) {
    return (
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="text-behavior-data-info">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0" />
          <span>Behavior data derived from All Pets and Interaction Summary reports</span>
        </div>
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400 hover-elevate rounded-sm px-1"
          onClick={onOpenModal}
          data-testid="badge-report-freshness"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>All locations current</span>
        </button>
      </div>
    );
  }
  
  if (dismissed) return null;
  
  return (
    <Alert className="mb-6 border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-500 relative">
      <Info className="h-4 w-4" />
      <button 
        type="button"
        className="absolute top-3 right-3 p-1 rounded-sm text-blue-600 dark:text-blue-400 rounded-sm"
        onClick={() => setDismissed(true)}
        data-testid="button-dismiss-behavior-alert"
      >
        <X className="h-4 w-4" />
      </button>
      <AlertTitle className="text-blue-800 dark:text-blue-200 pr-8" data-testid="text-behavior-data-info-stale">
        Behavior data derived from All Pets and Interaction Summary reports
        <span className="font-normal text-amber-700 dark:text-amber-400 ml-2">
          ({staleLocationCount} location{staleLocationCount !== 1 ? "s" : ""} with stale data)
        </span>
      </AlertTitle>
      <AlertDescription className="text-blue-700 dark:text-blue-300 flex flex-wrap items-center justify-between gap-2">
        <span>Upload new All Pets and Interaction Summary reports to see the latest behavior data.</span>
        <Button variant="outline" size="sm" onClick={onOpenModal} className="h-7 bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 ml-auto" data-testid="button-view-freshness-modal">
          View Details
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function AlertSummaryCard({ 
  title, 
  count, 
  icon: Icon, 
  color,
  isActive,
  onClick,
  testId,
  description,
}: { 
  title: string; 
  count: number; 
  icon: typeof AlertTriangle; 
  color: string;
  isActive: boolean;
  onClick: () => void;
  testId: string;
  description?: string;
}) {
  return (
    <Card 
      className={`cursor-pointer transition-all hover-elevate ${isActive ? 'ring-2 ring-primary' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${color}`} />
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="text-2xl font-bold" data-testid={`${testId}-count`}>{count}</div>
        <p className="text-xs text-muted-foreground">
          {count === 1 ? 'animal' : 'animals'}
        </p>
        {description && (
          <div className="mt-2 p-2 rounded bg-primary/10 border border-primary/20 text-[10px] text-primary leading-tight" data-testid={`${testId}-description`}>
            {description}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AlertBadges({ alerts, details }: { alerts: AnimalAlerts; details: AnimalAlertDetails }) {
  const badges: { icon: typeof AlertTriangle; label: string; tooltip: string; color: string; show: boolean }[] = [
    { 
      icon: AlertTriangle, 
      label: `Struggling (${details.strugglingNowCount} in 72h)`,
      tooltip: `Struggling Now: ${details.strugglingNowCount} high-severity interactions in the last 72 hours.`,
      color: "text-destructive", 
      show: alerts.struggling 
    },
    { 
      icon: TrendingDown, 
      label: "3d Worsening",
      tooltip: `Worsening Trend: The rate of high-severity interactions has increased by 20%+ compared to the prior 3-day period.`,
      color: "text-rose-500", 
      show: alerts.worsening 
    },
    { 
      icon: Flame, 
      label: `${details.streakDays}-day streak`,
      tooltip: `Behavior Streak: This animal has had at least one high-severity interaction for ${details.streakDays} consecutive days.`,
      color: "text-orange-500", 
      show: alerts.streak 
    },
    { 
      icon: TrendingUp, 
      label: details.dailySpikeMultiplier ? `Spike (${details.dailySpikeMultiplier.toFixed(1)}x)` : "Rate Spike",
      tooltip: details.dailySpikeMultiplier 
        ? `Daily Spike: Today's high-severity rate is ${details.dailySpikeMultiplier.toFixed(1)}x higher than the 7-day average.`
        : `Daily Spike: Today's high-severity rate is significantly higher than the 7-day baseline average.`,
      color: "text-amber-500", 
      show: alerts.spike 
    },
    { 
      icon: AlertCircle, 
      label: `${details.recentHighSeverityCount} high-sev (7d)`,
      tooltip: `High Severity Count: ${details.recentHighSeverityCount} high-severity interactions recorded in the past 7 days.`,
      color: "text-red-600", 
      show: alerts.highSeverity 
    },
    // Gate R4: flag signal badges
    {
      icon: FileText,
      label: "Long note flag",
      tooltip: "This animal has an interaction with an unusually long note that may need review.",
      color: "text-info",
      show: alerts.hasLongNoteFlag,
    },
    {
      icon: Tag,
      label: "Keyword flag",
      tooltip: "This animal has an interaction containing a flagged keyword.",
      color: "text-info",
      show: alerts.hasKeywordFlag,
    },
    {
      icon: Flag,
      label: "Highlighted",
      tooltip: "This animal has a manually highlighted interaction.",
      color: "text-highlight",
      show: alerts.hasHighlightFlag,
    },
    {
      icon: Clock,
      label: "Overdue",
      tooltip: "This animal has not had a recorded interaction within the expected window.",
      color: "text-warning",
      show: alerts.overdueForInteraction,
    },
  ];

  const activeBadges = badges.filter(b => b.show);

  if (activeBadges.length === 0) {
    return <span className="text-muted-foreground text-sm">None</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {activeBadges.map((badge, idx) => (
        <Tooltip key={idx}>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className={`gap-1 ${badge.color}`}
            >
              <badge.icon className="h-3 w-3" />
              <span className="text-xs">{badge.label}</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="text-sm">{badge.tooltip}</p>
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}

// Gate I8.20: Updated AnimalRow - clickable to open drawer, removed view icon
// Gate S19: added followedIds prop (hoisted from BehaviorInsights to avoid per-row hook call)
function AnimalRow({ animal, onClick, followedIds }: { animal: FlaggedAnimal; onClick: (animalId: string) => void; followedIds: string[] }) {
  const { resolveLabel } = useZoneLabels();
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(animal.animalId);
    }
  };
  
  return (
    <TableRow 
      data-testid={`row-animal-${animal.animalId}`}
      className="cursor-pointer hover:bg-muted/50"
      tabIndex={0}
      role="button"
      onClick={() => onClick(animal.animalId)}
      onKeyDown={handleKeyDown}
      aria-label={`View details for ${animal.name}`}
    >
      <TableCell>
        <AnimalCell
          animalId={animal.animalId}
          name={animal.name}
          species={animal.species}
          photoUrl={animal.photoUrl ?? null}
          inlineBadges={
            <>
              {followedIds.includes(animal.animalId) && (
                <Heart className="h-3 w-3 fill-rose-500 text-rose-500 shrink-0" />
              )}
              {animal.alerts.hasHighlightFlag && (
                <Flag className="h-3 w-3 fill-amber-500 text-amber-500 shrink-0" />
              )}
            </>
          }
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <MapPin className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm">{animal.location}</span>
        </div>
        {animal.zoneCode && (
          <div className="text-xs text-muted-foreground">{resolveLabel(animal.zoneCode)}</div>
        )}
      </TableCell>
      <TableCell data-testid={`cell-last-concern-at-${animal.animalId}`}>
        {animal.lastConcern ? (
          <span className="text-sm">{format(new Date(animal.lastConcern.date), "MMM d, yyyy")}</span>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </TableCell>
      <TableCell data-testid={`cell-last-concern-score-${animal.animalId}`}>
        {animal.lastConcern?.scoreCode ? (
          <Badge variant="outline" className="text-xs">
            {animal.lastConcern.scoreCode}
          </Badge>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </TableCell>
      <TableCell>
        <AlertBadges alerts={animal.alerts} details={animal.details} />
      </TableCell>
    </TableRow>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-12" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const LOCATION_LABELS: Record<string, string> = {
  SALEM: 'Salem',
  BOSTON: 'Boston',
  METHUEN: 'Methuen',
  CAPE: 'Cape',
};

const ADOPTION_LEVEL_LABELS: Record<string, string> = {
  L1: 'Level 1',
  L2: 'Level 2',
  L3: 'Level 3',
  L4: 'Level 4',
  missing: 'No Level',
};

const ALERT_TYPE_LABELS: Record<string, string> = {
  any: 'All Flagged',
  hasKeywordFlag: 'Keyword Match',
  hasLongNoteFlag: 'Long Note',
  hasHighlightFlag: 'Highlighted',
  overdueForInteraction: 'Overdue for Interaction',
  struggling: 'Struggling Now',
  streak: 'Score Streak',
  spike: 'Daily Spike',
  highSeverity: 'High Severity',
  worsening: 'Worsening Trend',
};

export default function BehaviorInsights() {
  usePageTitle("Behavior");
  const { resolveLabel } = useZoneLabels();
  const { data: followsData } = useFollowedAnimalIds();
  const followedIds = followsData?.animalIds ?? [];
  const { selectedShelter, setSelectedShelter, isSystemView } = useInsightsContext();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const [, setLocation] = useLocation();
  
  // Gate I8: Feature flag check for Workflow tab
  const workflowEnabled = isWorkflowDashboardEnabled();
  
  // Gate I8.6.1: Helper to update URL params without full page reload
  // Fix C: Added guard to prevent no-op writes and potential infinite loops
  const updateUrlParams = useCallback((updates: Record<string, string | null>) => {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === '') {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    }
    const newSearch = params.toString();
    const newPath = `/behavior${newSearch ? `?${newSearch}` : ''}`;
    // Guard: Skip if URL is already at the target path
    const currentPath = window.location.pathname + window.location.search;
    if (newPath === currentPath) {
      return;
    }
    setLocation(newPath, { replace: true });
  }, [setLocation]);
  
  const [analyticsDays, setAnalyticsDays] = useState<number>(14);

  // Tab state - defaults to "workflow" when enabled, otherwise "signals"
  const [activeTab, setActiveTab] = useState<string>(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "signals") return "signals";
    if (tabParam === "analytics") return "analytics";
    if (tabParam === "workflow" && workflowEnabled) return "workflow";
    return workflowEnabled ? "workflow" : "signals";
  });
  
  const today = format(new Date(), "yyyy-MM-dd");
  const sevenDaysAgo = format(subDays(new Date(), 7), "yyyy-MM-dd");
  
  const [alertType, setAlertType] = useState<AlertType>(
    (searchParams.get("alertType") as AlertType) || null
  );
  const [severityFilter, setSeverityFilter] = useState<string | null>(
    searchParams.get("severity") || (searchParams.get("alertType") ? null : "mh")
  );
  const [speciesFilter, setSpeciesFilter] = useState<string>(
    searchParams.get("species") || "all"
  );
  const [startDate, setStartDate] = useState(searchParams.get("startDate") || sevenDaysAgo);
  const [endDate, setEndDate] = useState(searchParams.get("endDate") || today);
  const [showHints, setShowHints] = useState<boolean>(() => {
    try { return localStorage.getItem(BEHAVIOR_HINTS_STORAGE_KEY) === "true"; } catch { return false; }
  });
  const handleShowHintsChange = useCallback((checked: boolean) => {
    setShowHints(checked);
    try { localStorage.setItem(BEHAVIOR_HINTS_STORAGE_KEY, String(checked)); } catch {}
  }, []);
  const [alertsLocationFilter, setAlertsLocationFilter] = useState<string>(
    searchParams.get("alertsLocation") || (isSystemView ? "all" : selectedShelter.toUpperCase())
  );
  const [alertsEvalStatusFilter, setAlertsEvalStatusFilter] = useState<string>(
    searchParams.get("alertsEvalStatus") || "all"
  );
  const [alertsAdoptionLevelFilter, setAlertsAdoptionLevelFilter] = useState<string>(
    searchParams.get("alertsAdoptionLevel") || "all"
  );
  const [alertsFilterCollapsed, setAlertsFilterCollapsed] = useState<boolean>(() => window.innerWidth < 640);

  // Gate I8: Workflow tab state (namespaced to avoid collision with Alerts filters)
  const [workflowLocationFilter, setWorkflowLocationFilter] = useState<string>(
    searchParams.get("workflowLocation") || "all"
  );
  const [workflowSpeciesFilter, setWorkflowSpeciesFilter] = useState<string>(
    searchParams.get("workflowSpecies") || "all"
  );
  const [workflowEvalStatusFilter, setWorkflowEvalStatusFilter] = useState<string>(
    searchParams.get("workflowEvalStatus") || "all"
  );
  const [workflowAdoptionLevelFilter, setWorkflowAdoptionLevelFilter] = useState<string>(
    searchParams.get("workflowAdoptionLevel") || "all"
  );
  // Gate I8.6.1: Initialize bucket from URL with strong validation (Fix D)
  // Validates that bucket key has valid track AND valid eval status
  const validateBucketKey = (key: string | null): BucketKey | null => {
    if (!key || !key.includes(':')) return null;
    const [track, status] = key.split(':') as [string, string];
    const isValidTrack = VALID_TRACKS.includes(track as Track);
    const isValidStatus = VALID_EVAL_STATUSES.includes(status as EvalStatus);
    if (isValidTrack && isValidStatus) {
      return key as BucketKey;
    }
    return null;
  };
  const initialBucket = validateBucketKey(searchParams.get("workflowBucket"));
  const [selectedBucket, setSelectedBucket] = useState<BucketKey | null>(initialBucket);
  // Gate I8.6.1: Initialize view mode from URL (I8.15: added 'list')
  const initialViewMode = searchParams.get("workflowView");
  const [workflowViewMode, setWorkflowViewMode] = useState<WorkflowViewMode>(
    initialViewMode === 'board' ? 'board' : initialViewMode === 'list' ? 'list' : 'grid'
  );

  // Gate I8.15: List view pagination state (in-memory, no refetch)
  const initialWorkflowPage = parseInt(searchParams.get("workflowPage") || "1", 10);
  const initialWorkflowPageSize = parseInt(searchParams.get("workflowPageSize") || "25", 10);
  const [workflowPage, setWorkflowPage] = useState<number>(
    isNaN(initialWorkflowPage) || initialWorkflowPage < 1 ? 1 : initialWorkflowPage
  );
  const [workflowPageSize, setWorkflowPageSize] = useState<number>(
    [10, 25, 50, 100].includes(initialWorkflowPageSize) ? initialWorkflowPageSize : 25
  );

  // Gate I8.15.1: List view column sorting state
  // null = use workflowSort dropdown ordering; non-null = use column header sort
  const [listSortColumn, setListSortColumn] = useState<ListSortColumn>(null);
  const [listSortDirection, setListSortDirection] = useState<ListSortDirection>('asc');

  // Gate I8.15.1: Toggle sort on column header click
  const handleListColumnSort = useCallback((column: Exclude<ListSortColumn, null>) => {
    if (listSortColumn === column) {
      setListSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setListSortColumn(column);
      setListSortDirection('asc');
    }
    setWorkflowPage(1); // Reset to first page on sort change
  }, [listSortColumn]);

  // Gate I8 Phase 7: Needs Attention toggle (10-day rule) + Sorting
  // Attention filter: show only AVAILABLE track animals with 10+ days since last interaction
  const [workflowSummaryFilter, setWorkflowSummaryFilter] = useState<WorkflowSummaryTileFilter>(null);

  const [workflowAttention, setWorkflowAttention] = useState<boolean>(
    searchParams.get("workflowAttention") === "1"
  );
  const [filterCollapsed, setFilterCollapsed] = useState<boolean>(() => window.innerWidth < 640);
  const [evalSummaryCollapsed, setEvalSummaryCollapsed] = useState(true);
  // Sort: "overdue" (default, higher daysSinceLastInteraction first) or "name" (A→Z)
  type WorkflowSort = 'overdue' | 'name';
  const initialSort = searchParams.get("workflowSort");
  const [workflowSort, setWorkflowSort] = useState<WorkflowSort>(
    initialSort === 'name' ? 'name' : 'overdue'
  );

  // Reset list column sort when workflowSort changes (so dropdown takes effect in List view)
  useEffect(() => {
    setListSortColumn(null);
  }, [workflowSort]);

  // Gate I8.7.1: Ref for bucket detail section (scroll target)
  const bucketDetailRef = useRef<HTMLDivElement>(null);

  // Gate I8.11: Workflow Search state
  const { highlightedId: highlightedAnimalId, trigger: triggerHighlight } = useScrollHighlight();
  const [searchDrawerAnimalId, setSearchDrawerAnimalId] = useState<string | null>(null);
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);

  // Gate I8.6.1 Fix E: Track previous tab to detect tab change direction
  const [prevTab, setPrevTab] = useState<string>(activeTab);
  
  // Gate I8.6.1 Fix E: Clear workflow state when leaving Workflow tab (deterministic behavior)
  // This ensures switching back to Workflow does NOT restore an old bucket unless it's in the URL
  useEffect(() => {
    if (prevTab === 'workflow' && activeTab !== 'workflow') {
      // Leaving workflow tab - clear bucket selection and reset view to grid
      setSelectedBucket(null);
      setWorkflowViewMode('grid');
    }
    setPrevTab(activeTab);
  }, [activeTab, prevTab]);

  // Gate I8.6.1: Sync all workflow-related state to URL in a single effect
  // Consolidating to prevent race conditions between separate useEffect hooks
  // Phase 7: Added workflowAttention and workflowSort
  // I8.15: Added workflowPage and workflowPageSize for list view pagination
  useEffect(() => {
    const updates: Record<string, string | null> = {
      tab: activeTab,
    };
    
    if (activeTab === 'workflow') {
      updates.workflowLocation = workflowLocationFilter === 'all' ? null : workflowLocationFilter;
      updates.workflowSpecies = workflowSpeciesFilter === 'all' ? null : workflowSpeciesFilter;
      updates.workflowEvalStatus = workflowEvalStatusFilter === 'all' ? null : workflowEvalStatusFilter;
      updates.workflowAdoptionLevel = workflowAdoptionLevelFilter === 'all' ? null : workflowAdoptionLevelFilter;
      updates.workflowView = workflowViewMode === 'grid' ? null : workflowViewMode;
      updates.workflowBucket = selectedBucket;
      // Phase 7: Persist attention toggle and sort
      updates.workflowAttention = workflowAttention ? '1' : null;
      updates.workflowSort = workflowSort === 'name' ? 'name' : null; // 'overdue' is default, omit from URL
      // I8.15: Persist list pagination (only when in list view)
      if (workflowViewMode === 'list') {
        updates.workflowPage = workflowPage > 1 ? String(workflowPage) : null;
        updates.workflowPageSize = workflowPageSize !== 25 ? String(workflowPageSize) : null;
      } else {
        updates.workflowPage = null;
        updates.workflowPageSize = null;
      }
    } else {
      // Clear workflow params when not on workflow tab
      updates.workflowLocation = null;
      updates.workflowSpecies = null;
      updates.workflowEvalStatus = null;
      updates.workflowAdoptionLevel = null;
      updates.workflowView = null;
      updates.workflowBucket = null;
      updates.workflowAttention = null;
      updates.workflowSort = null;
      updates.workflowPage = null;
      updates.workflowPageSize = null;
    }
    
    updateUrlParams(updates);
  }, [activeTab, workflowLocationFilter, workflowSpeciesFilter, workflowEvalStatusFilter, workflowAdoptionLevelFilter, workflowViewMode, selectedBucket, workflowAttention, workflowSort, workflowPage, workflowPageSize, updateUrlParams]);

  // Gate I8.6.1: Sync sub-nav location to workflow location when on Workflow tab
  useEffect(() => {
    if (activeTab === 'workflow' && workflowEnabled) {
      const mappedLocation = selectedShelter === 'system' ? 'all' : selectedShelter.toUpperCase();
      if (mappedLocation !== workflowLocationFilter) {
        setWorkflowLocationFilter(mappedLocation);
        setSelectedBucket(null);
      }
    }
  }, [activeTab, selectedShelter, workflowEnabled]);

  // Sync sub-nav location to alerts location filter when on Signals tab
  useEffect(() => {
    if (activeTab === 'signals') {
      const mappedLocation = selectedShelter === 'system' ? 'all' : selectedShelter.toUpperCase();
      if (mappedLocation !== alertsLocationFilter) {
        setAlertsLocationFilter(mappedLocation);
      }
    }
  }, [activeTab, selectedShelter]);

  // Gate I8.7.1: Scroll to bucket detail when bucket is selected in grid view
  useEffect(() => {
    // Only scroll when: grid view and bucket is selected
    if (workflowViewMode !== 'grid' || !selectedBucket) return;
    
    // Defer scroll to allow DOM to settle after conditional render
    const scrollTimeout = setTimeout(() => {
      if (!bucketDetailRef.current) return;
      
      // Respect prefers-reduced-motion for accessibility
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      // Use scrollIntoView with scroll-margin-top CSS for header offset
      // The CSS scroll-margin-top handles the sticky header offset automatically
      bucketDetailRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    }, 100); // Increased delay for layout to settle
    
    return () => clearTimeout(scrollTimeout);
  }, [selectedBucket, workflowViewMode]);


  const { locations } = useActiveLocations();

  const getLocationLabel = (code: string): string => {
    if (code === "system") return "System-wide";
    const loc = locations.find((l) => l.code === code);
    return loc?.label || code;
  };

  const pageTitle = getLocationLabel(selectedShelter);

  const handleDatePreset = (days: number) => {
    setStartDate(format(subDays(new Date(), days), "yyyy-MM-dd"));
    setEndDate(format(new Date(), "yyyy-MM-dd"));
  };

  const handleAllTimePreset = () => {
    setStartDate("2020-01-01");
    setEndDate(format(new Date(), "yyyy-MM-dd"));
  };

  const isAllTimeSelected = startDate === "2020-01-01" && endDate === today;

  const { data: thresholds } = useQuery<AlertThresholds>({
    queryKey: ['/api/behavior-alerts/thresholds'],
  });

  // Gate I8: Workflow Dashboard query (only runs when enabled and on workflow tab)
  const { 
    data: workflowData, 
    isLoading: workflowLoading, 
    error: workflowError,
    refetch: refetchWorkflow
  } = useQuery<WorkflowDashboardResponse>({
    queryKey: ['/api/behavior/workflow-dashboard', workflowLocationFilter, workflowSpeciesFilter],
    queryFn: () => fetchWorkflowDashboard({
      location: workflowLocationFilter,
      species: workflowSpeciesFilter
    }),
    enabled: workflowEnabled && activeTab === 'workflow',
  });

  // Gate I8.9: Freshness data for stale report alerts
  const [freshnessModalOpen, setFreshnessModalOpen] = useState(false);
  const { allPets: allPetsFreshness, interaction: interactionFreshness } = useReportFreshness();

  // Gate I8.9: Build stale locations list for workflow alert
  const workflowStaleLocations = allPetsFreshness.staleLocations;

  // Stale location count for warning banner
  // Combines all-pets and interaction report staleness (any location stale in either)
  const alertsStaleLocationCount = useMemo(() => {
    const staleSet = new Set<string>();
    allPetsFreshness.staleLocations.forEach(loc => staleSet.add(loc.locationCode));
    interactionFreshness.staleLocations.forEach(loc => staleSet.add(loc.locationCode));
    return staleSet.size;
  }, [allPetsFreshness.staleLocations, interactionFreshness.staleLocations]);

  // Gate I8.9: Determine which stale locations to show based on workflow filter
  const workflowStaleMsgLocations = useMemo(() => {
    if (workflowLocationFilter !== 'all') {
      const stale = workflowStaleLocations.find(loc => loc.locationCode === workflowLocationFilter);
      return stale ? [stale] : [];
    }
    return workflowStaleLocations;
  }, [workflowLocationFilter, workflowStaleLocations]);

  // Phase 7: Helper to filter and sort workflow items
  const filterAndSortItems = (items: WorkflowDashboardItem[]): WorkflowDashboardItem[] => {
    let result = [...items];
    
    // Apply eval status filter
    if (workflowEvalStatusFilter !== 'all') {
      result = result.filter(item => item.evalStatus === workflowEvalStatusFilter);
    }
    
    // Apply adoption level filter
    if (workflowAdoptionLevelFilter !== 'all') {
      if (workflowAdoptionLevelFilter === 'missing') {
        result = result.filter(item => !item.adoptionLevel);
      } else {
        result = result.filter(item => item.adoptionLevel === workflowAdoptionLevelFilter);
      }
    }
    
    // Apply attention filter: show only items where hasAttentionFlag is true
    // hasAttentionFlag is computed on backend: AVAILABLE track + 10+ days since last interaction
    if (workflowAttention) {
      result = result.filter(item => item.hasAttentionFlag === true);
    }
    
    // Apply sort
    if (workflowSort === 'name') {
      result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      // 'overdue' - earliest intakeDate first (longest time in shelter), nulls last
      result.sort((a, b) => {
        const aDate = a.intakeDate ? new Date(a.intakeDate).getTime() : Infinity;
        const bDate = b.intakeDate ? new Date(b.intakeDate).getTime() : Infinity;
        return aDate - bDate; // Ascending (earliest intake = longest wait first)
      });
    }
    
    return result;
  };

  // Phase 7: Base items — all animals after filterAndSortItems, no tile filter applied.
  // Gate S9: Passed to WorkflowEvalSummary so tile badge counts always reflect the full
  // population (respecting location/species/attention/evalStatus/adoptionLevel, but NOT
  // the active tile). This keeps badges stable when switching tiles.
  const baseWorkflowItems = useMemo(() => {
    if (!workflowData) return [];
    return filterAndSortItems(workflowData.items);
  }, [workflowData, workflowEvalStatusFilter, workflowAdoptionLevelFilter, workflowAttention, workflowSort]);

  // Phase 7: Filtered items — base items with tile filter applied. Drives the content list.
  // Gate S9: workflowSummaryFilter applied as a final step after base sort.
  // Uses matchesWorkflowSummaryFilter (imported from WorkflowEvalSummary) as the single
  // source of truth — tile count predicates and list filtering share the same function,
  // preventing predicate drift between the two code paths (see D-F5 follow-up).
  const filteredWorkflowItems = useMemo(() => {
    if (!workflowSummaryFilter) return baseWorkflowItems;
    return baseWorkflowItems.filter(item => matchesWorkflowSummaryFilter(item, workflowSummaryFilter));
  }, [baseWorkflowItems, workflowSummaryFilter]);

  // Gate I8: Get items for selected bucket.
  // S9-21: Filters from filteredWorkflowItems (tile-filter-aware) so bucket drill-down narrows
  // within the active tile filter instead of replacing it.
  const selectedBucketItems = useMemo(() => {
    if (!selectedBucket) return [];
    return filteredWorkflowItems.filter(item => item.bucketKey === selectedBucket);
  }, [filteredWorkflowItems, selectedBucket]);

  // Phase 7: Compute bucket counts from filtered items (for Grid view consistency)
  const filteredBucketCounts = useMemo(() => {
    const counts: Record<BucketKey, number> = {} as Record<BucketKey, number>;
    for (const item of filteredWorkflowItems) {
      counts[item.bucketKey] = (counts[item.bucketKey] || 0) + 1;
    }
    return counts;
  }, [filteredWorkflowItems]);

  // Gate I8.15.1: Sort items for list view by selected column, then paginate
  // When listSortColumn is null, use the workflowSort dropdown ordering (already applied in filteredWorkflowItems)
  const listViewSortedItems = useMemo(() => {
    // If no column sort is active, use the dropdown-sorted items as-is
    if (listSortColumn === null) {
      return filteredWorkflowItems;
    }
    
    // Otherwise, apply column-based sorting
    const items = [...filteredWorkflowItems];
    
    items.sort((a, b) => {
      let cmp = 0;
      switch (listSortColumn) {
        case 'animal':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'track':
          cmp = (a.track || '').localeCompare(b.track || '');
          break;
        case 'evalStatus':
          cmp = (a.evalStatus || '').localeCompare(b.evalStatus || '');
          break;
        case 'adoptionLevel':
          // Sort by level number (L1 < L2 < L3 < L4), nulls last
          const levelA = a.adoptionLevel ? parseInt(a.adoptionLevel.replace(/\D/g, '') || '99') : 99;
          const levelB = b.adoptionLevel ? parseInt(b.adoptionLevel.replace(/\D/g, '') || '99') : 99;
          cmp = levelA - levelB;
          break;
        case 'location':
          cmp = (a.location || '').localeCompare(b.location || '');
          break;
        case 'intakeDate':
          // Sort by intake date, nulls last
          const dateA = a.intakeDate ? new Date(a.intakeDate).getTime() : Infinity;
          const dateB = b.intakeDate ? new Date(b.intakeDate).getTime() : Infinity;
          cmp = dateA - dateB;
          break;
        case 'onHold':
          // On hold first (true before false)
          cmp = (a.isOnHold === b.isOnHold) ? 0 : a.isOnHold ? -1 : 1;
          break;
        case 'attention':
          // true (needs attention) before false
          cmp = (a.hasAttentionFlag === b.hasAttentionFlag) ? 0 : a.hasAttentionFlag ? -1 : 1;
          break;
        case 'evalChecklist':
          // Sort by outstanding items (total - done) descending; not-activated (evalTotal=0) sort last
          const outstandingA = a.evalActivated ? (a.evalTotal - a.evalDone) : Infinity;
          const outstandingB = b.evalActivated ? (b.evalTotal - b.evalDone) : Infinity;
          cmp = outstandingA - outstandingB;
          break;
      }
      return listSortDirection === 'asc' ? cmp : -cmp;
    });
    
    return items;
  }, [filteredWorkflowItems, listSortColumn, listSortDirection]);

  // Gate I8.15: Paginated items for List view (in-memory pagination)
  const listViewPagination = useMemo(() => {
    const total = listViewSortedItems.length;
    const totalPages = Math.max(1, Math.ceil(total / workflowPageSize));
    const validPage = Math.min(Math.max(1, workflowPage), totalPages);
    const startIndex = (validPage - 1) * workflowPageSize;
    const endIndex = Math.min(startIndex + workflowPageSize, total);
    const paginatedItems = listViewSortedItems.slice(startIndex, endIndex);
    return {
      items: paginatedItems,
      total,
      totalPages,
      currentPage: validPage,
      startIndex: total > 0 ? startIndex + 1 : 0,
      endIndex,
    };
  }, [listViewSortedItems, workflowPage, workflowPageSize]);

  // Gate I8.15: Reset page to 1 when filters change (prevent showing empty page)
  useEffect(() => {
    if (workflowPage > listViewPagination.totalPages) {
      setWorkflowPage(1);
    }
  }, [filteredWorkflowItems.length, workflowPageSize]);

  // Gate I8.15: State for list row drawer
  const [listDrawerAnimalId, setListDrawerAnimalId] = useState<string | null>(null);
  const [listDrawerOpen, setListDrawerOpen] = useState(false);

  // Gate I8.15: Handle list row click to open drawer
  const handleListRowClick = useCallback((item: WorkflowDashboardItem) => {
    setListDrawerAnimalId(item.animalId);
    setListDrawerOpen(true);
  }, []);

  // Gate I8.20: State for Alerts tab drawer
  const [alertsDrawerAnimalId, setAlertsDrawerAnimalId] = useState<string | null>(null);
  const [alertsDrawerOpen, setAlertsDrawerOpen] = useState(false);

  // Gate I8.20: Handle alerts table row click to open drawer
  const handleAlertsRowClick = useCallback((animalId: string) => {
    setAlertsDrawerAnimalId(animalId);
    setAlertsDrawerOpen(true);
  }, []);

  // Gate I8.11: Jump to animal from search
  // S9-21: No longer clears workflowSummaryFilter on bucket select — tile filter and bucket combine.
  const handleSearchJumpToAnimal = useCallback((animalId: string, track: Track, evalStatus: EvalStatus) => {
    if (workflowViewMode === 'grid') {
      const bucketKey = buildBucketKey(track, evalStatus);
      setSelectedBucket(bucketKey);
    }

    if (workflowViewMode === 'list') {
      const idx = listViewSortedItems.findIndex((item) => item.animalId === animalId);
      if (idx >= 0) {
        const targetPage = Math.floor(idx / workflowPageSize) + 1;
        setWorkflowPage(targetPage);
      }
    }

    triggerHighlight(animalId);

    const scrollDelay = workflowViewMode === 'list' ? 250 : 150;
    setTimeout(() => {
      let elementId: string;
      if (workflowViewMode === 'grid') {
        elementId = `workflow-bucket-animal-${animalId}`;
      } else if (workflowViewMode === 'board') {
        elementId = `workflow-board-animal-${animalId}`;
      } else {
        elementId = `workflow-list-animal-${animalId}`;
      }
      const element = document.getElementById(elementId);
      if (element) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        element.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'center'
        });
      }
    }, scrollDelay);
  }, [workflowViewMode, listViewSortedItems, workflowPageSize, triggerHighlight]);

  // Gate I8.11: Open animal details from search
  const handleSearchOpenDetails = useCallback((animalId: string) => {
    setSearchDrawerAnimalId(animalId);
    setSearchDrawerOpen(true);
  }, []);

  const { data, isLoading, error, isFetching } = useQuery<BehaviorAlertsResponse>({
    queryKey: ['/api/behavior-alerts', alertType, severityFilter, alertsLocationFilter, speciesFilter, alertsEvalStatusFilter, alertsAdoptionLevelFilter, startDate, endDate],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (alertType) params.set("alertType", alertType);
      if (severityFilter) params.set("severity", severityFilter);
      if (alertsLocationFilter && alertsLocationFilter !== "all") params.set("location", alertsLocationFilter);
      if (speciesFilter && speciesFilter !== "all") params.set("species", speciesFilter);
      if (alertsEvalStatusFilter && alertsEvalStatusFilter !== "all") params.set("evalStatus", alertsEvalStatusFilter);
      if (alertsAdoptionLevelFilter && alertsAdoptionLevelFilter !== "all") params.set("adoptionLevel", alertsAdoptionLevelFilter);
      params.set("startDate", startDate);
      params.set("endDate", endDate);
      
      const url = `/api/behavior-alerts?${params.toString()}`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch behavior alerts');
      return response.json();
    },
  });

  const handleAlertCardClick = (type: AlertType) => {
    setAlertType(alertType === type ? null : type);
    if (type !== null) setSeverityFilter(null);
  };

  const handleSeverityCardClick = () => {
    setSeverityFilter(severityFilter === 'mh' ? null : 'mh');
    if (severityFilter !== 'mh') setAlertType(null);
  };

  const clearFilters = () => {
    setAlertType(null);
    setSeverityFilter(null);
    setSpeciesFilter("all");
    setAlertsLocationFilter("all");
    setAlertsEvalStatusFilter("all");
    setAlertsAdoptionLevelFilter("all");
    setStartDate(sevenDaysAgo);
    setEndDate(today);
  };

  const hasActiveFilters = alertType !== null || severityFilter !== null || speciesFilter !== "all" || alertsLocationFilter !== "all" || alertsEvalStatusFilter !== "all" || alertsAdoptionLevelFilter !== "all" || startDate !== sevenDaysAgo || endDate !== today;

  const alertsDescription = useMemo(() => {
    const parts: string[] = [];
    parts.push(LOCATION_LABELS[alertsLocationFilter] ?? 'All locations');
    if (speciesFilter !== 'all') parts.push(speciesFilter === 'DOG' ? 'Dogs' : 'Cats');
    if (alertsEvalStatusFilter !== 'all') parts.push((EVAL_STATUS_LABELS as Record<string, string>)[alertsEvalStatusFilter] ?? alertsEvalStatusFilter);
    if (alertsAdoptionLevelFilter !== 'all') parts.push(ADOPTION_LEVEL_LABELS[alertsAdoptionLevelFilter] ?? alertsAdoptionLevelFilter);
    if (alertType) parts.push(ALERT_TYPE_LABELS[alertType] ?? alertType);
    if (severityFilter === 'mh') parts.push('High concern');
    return parts.join(', ');
  }, [alertsLocationFilter, speciesFilter, alertsEvalStatusFilter, alertsAdoptionLevelFilter, alertType, severityFilter]);

  const workflowDescription = useMemo(() => {
    const parts: string[] = [];
    parts.push(LOCATION_LABELS[workflowLocationFilter] ?? 'All locations');
    if (workflowSpeciesFilter !== 'all') parts.push(workflowSpeciesFilter === 'DOG' ? 'Dogs' : 'Cats');
    if (workflowEvalStatusFilter !== 'all') parts.push((EVAL_STATUS_LABELS as Record<string, string>)[workflowEvalStatusFilter] ?? workflowEvalStatusFilter);
    if (workflowAdoptionLevelFilter !== 'all') parts.push(ADOPTION_LEVEL_LABELS[workflowAdoptionLevelFilter] ?? workflowAdoptionLevelFilter);
    if (workflowAttention) parts.push('Needs attention');
    return parts.join(', ');
  }, [workflowLocationFilter, workflowSpeciesFilter, workflowEvalStatusFilter, workflowAdoptionLevelFilter, workflowAttention]);

  if (isLoading) {
    return (
      <InsightsPageWrapper>
        <div className="space-y-6">
          <PageHeader
            breadcrumb={[{ label: "Monitoring" }, { label: "Behavior" }]}
            title={
            <span className="flex items-center gap-2" data-testid="heading-behavior">
              <Award className="w-6 h-6 text-primary" />
              Behavior
            </span>
          }
            hideBreadcrumbOnMobile
          />
          <LoadingSkeleton />
        </div>
      </InsightsPageWrapper>
    );
  }

  if (error) {
    return (
      <InsightsPageWrapper>
        <div className="space-y-6">
          <PageHeader
            breadcrumb={[{ label: "Monitoring" }, { label: "Behavior" }]}
            title={
            <span className="flex items-center gap-2" data-testid="heading-behavior">
              <Award className="w-6 h-6 text-primary" />
              Behavior
            </span>
          }
            hideBreadcrumbOnMobile
          />
          <Card>
            <CardContent className="py-10 text-center">
              <AlertCircle className="h-10 w-10 mx-auto text-destructive mb-4" />
              <p className="text-muted-foreground">Failed to load behavior signals</p>
            </CardContent>
          </Card>
        </div>
      </InsightsPageWrapper>
    );
  }

  const summary = data?.summary || { total: 0, flagged: 0, struggling: 0, worsening: 0, streak: 0, spike: 0, highSeverity: 0, moderateHighConcern: { interactions: 0, animals: 0 }, hasLongNoteFlag: 0, hasKeywordFlag: 0, hasHighlightFlag: 0, overdueForInteraction: 0 };
  const animals = data?.animals || [];

  return (
    <InsightsPageWrapper>
      <div className="space-y-6" data-testid="page-behavior-insights">
        <PageHeader
          breadcrumb={[{ label: "Monitoring" }, { label: "Behavior" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-behavior">
              <Award className="w-6 h-6 text-primary" />
              Behavior
            </span>
          }
          hideBreadcrumbOnMobile
          actions={null}
        />

        <InteractionDataWarning
          staleLocationCount={alertsStaleLocationCount}
          onOpenModal={() => setFreshnessModalOpen(true)}
        />

        <ReportFreshnessModal
          open={freshnessModalOpen}
          onOpenChange={setFreshnessModalOpen}
          staleLocations={[...allPetsFreshness.staleLocations.map(l => ({ ...l, sourceReportType: "allPets" as const })), ...interactionFreshness.staleLocations.map(l => ({ ...l, sourceReportType: "interaction" as const }))]}
          locationsWithGaps={[...allPetsFreshness.locationsWithGaps.map(l => ({ ...l, sourceReportType: "allPets" as const })), ...interactionFreshness.locationsWithGaps.map(l => ({ ...l, sourceReportType: "interaction" as const }))]}
          reportTypes={["allPets", "interaction"]}
          allPetsLocations={allPetsFreshness.locations}
          interactionLocations={interactionFreshness.locations}
        />

        {/* Gate I8: Tabs for Signals (default) and Workflow Dashboard (gated) */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between gap-2">
          <TabsList className="h-11 p-1" data-testid="tabs-behavior">
            {workflowEnabled && (
              <TabsTrigger 
                value="workflow" 
                className="h-9 px-4 text-sm font-medium gap-2"
                data-testid="tab-workflow"
              >
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden sm:inline">Workflow</span>
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="signals" 
              className="h-9 px-4 text-sm font-medium gap-2"
              data-testid="tab-behavior-signals"
            >
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Signals</span>
            </TabsTrigger>
            <TabsTrigger 
              value="analytics" 
              className="h-9 px-4 text-sm font-medium gap-2"
              data-testid="tab-behavior-analytics"
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Insights</span>
            </TabsTrigger>
          </TabsList>
          <HintsToggle
            id="behavior-show-hints"
            checked={showHints}
            onCheckedChange={handleShowHintsChange}
            label={activeTab === "workflow" ? "Workflow Hints" : activeTab === "signals" ? "Signals Hints" : "Insights Hints"}
            testId="switch-show-hints"
          />
          </div>

          {/* Signals Tab Content */}
          <TabsContent value="signals" className="space-y-6 mt-4">
            {/* Sticky filter bar - matching Workflow tab pattern */}
            <div className="sticky top-0 z-40 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-4 border-b bg-muted/80 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
            <Card>
              <CardContent className="p-5">
                <CollapsibleFilterHeader
                  collapsed={alertsFilterCollapsed}
                  onToggle={() => setAlertsFilterCollapsed((c) => !c)}
                  description={alertsDescription}
                  hasActiveFilters={hasActiveFilters}
                  onClearFilters={clearFilters}
                  clearLabel="Clear Filters"
                  descriptionTestId="text-alerts-filter-description"
                  clearTestId="button-clear-filters"
                >
                  <div className="flex flex-wrap gap-4" data-testid="alerts-filters">
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-[140px]"
                        data-testid="input-start-date"
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-[140px]"
                        data-testid="input-end-date"
                      />
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Button
                        variant={startDate === sevenDaysAgo && endDate === today ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleDatePreset(7)}
                        data-testid="button-preset-7d"
                      >
                        7d
                      </Button>
                      <Button
                        variant={startDate === format(subDays(new Date(), 14), "yyyy-MM-dd") && endDate === today ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleDatePreset(14)}
                        data-testid="button-preset-14d"
                      >
                        14d
                      </Button>
                      <Button
                        variant={startDate === format(subDays(new Date(), 30), "yyyy-MM-dd") && endDate === today ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleDatePreset(30)}
                        data-testid="button-preset-30d"
                      >
                        30d
                      </Button>
                      <Button
                        variant={isAllTimeSelected ? "default" : "outline"}
                        size="sm"
                        onClick={handleAllTimePreset}
                        data-testid="button-preset-all-time"
                      >
                        All
                      </Button>
                    </div>

                    <Select 
                      value={alertsLocationFilter} 
                      onValueChange={(value) => {
                        setAlertsLocationFilter(value);
                        if (activeTab === 'signals') {
                          const mappedShelter = value === 'all' ? 'system' : value;
                          setSelectedShelter(mappedShelter);
                        }
                      }}
                    >
                      <SelectTrigger className="w-auto min-w-[120px]" data-testid="select-alerts-location">
                        <MapPin className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                        <SelectValue placeholder="Location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Locations</SelectItem>
                        <SelectItem value="SALEM">Salem</SelectItem>
                        <SelectItem value="BOSTON">Boston</SelectItem>
                        <SelectItem value="METHUEN">Methuen</SelectItem>
                        <SelectItem value="CAPE">Cape</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={speciesFilter} onValueChange={setSpeciesFilter}>
                      <SelectTrigger className="w-auto min-w-[100px]" data-testid="select-species">
                        <PawPrint className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                        <SelectValue placeholder="Species" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Species</SelectItem>
                        <SelectItem value="DOG">Dogs</SelectItem>
                        <SelectItem value="CAT">Cats</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select 
                      value={alertsEvalStatusFilter} 
                      onValueChange={(value) => {
                        setAlertsEvalStatusFilter(value);
                      }}
                    >
                      <SelectTrigger className="w-auto min-w-[140px]" data-testid="select-alerts-eval-status">
                        <ClipboardCheck className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                        <SelectValue placeholder="Evaluation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Evaluations</SelectItem>
                        <SelectItem value="INCOMPLETE">Incomplete</SelectItem>
                        <SelectItem value="NEEDS_REVIEW">Needs Review</SelectItem>
                        <SelectItem value="NEEDS_RECS">Needs Recs</SelectItem>
                        <SelectItem value="COMPLETE">Complete</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select 
                      value={alertsAdoptionLevelFilter} 
                      onValueChange={(value) => {
                        setAlertsAdoptionLevelFilter(value);
                      }}
                    >
                      <SelectTrigger className="w-auto min-w-[140px]" data-testid="select-alerts-adoption-level">
                        <Award className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                        <SelectValue placeholder="Adoption Level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Levels</SelectItem>
                        <SelectItem value="L1">Level 1</SelectItem>
                        <SelectItem value="L2">Level 2</SelectItem>
                        <SelectItem value="L3">Level 3</SelectItem>
                        <SelectItem value="L4">Level 4</SelectItem>
                        <SelectItem value="missing">No Level</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CollapsibleFilterHeader>
              </CardContent>
            </Card>
            </div>

            {/* RESULTS section - matching Workflow tab pattern */}
            <div className="flex flex-col gap-3 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Showing</h3>
                  <span className="text-sm text-muted-foreground">—</span>
                  <span className="text-sm text-foreground" data-testid="text-alerts-results-location">
                    {alertsLocationFilter === 'all' ? 'All locations' : alertsLocationFilter}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <ReportActionItems
                    currentLocation={selectedShelter}
                    defaultReportType="interaction_summary"
                    showOtherType={true}
                  />
                </div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Behavior signals are automatically computed from ingested interaction data, highlighting animals whose recent behavior shows patterns worth attention.
            </p>

            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
              <AlertSummaryCard
                title="High Concern"
                count={summary.moderateHighConcern?.interactions ?? 0}
                icon={AlertTriangle}
                color="text-purple-600"
                isActive={severityFilter === "mh"}
                onClick={handleSeverityCardClick}
                testId="card-moderate-high-concern"
                description={showHints ? `Interactions rated High (4-5) concern.` : undefined}
              />
              <AlertSummaryCard
                title="All Flagged"
                count={summary.flagged}
                icon={AlertCircle}
                color="text-destructive"
                isActive={alertType === "any"}
                onClick={() => handleAlertCardClick("any")}
                testId="card-all-flagged"
                description={showHints ? "Animals showing one or more behavior signals — automatically detected patterns like worsening trends, streaks, or spikes in high-severity interactions." : undefined}
              />
              <AlertSummaryCard
                title="Struggling Now"
                count={summary.struggling}
                icon={AlertTriangle}
                color="text-destructive"
                isActive={alertType === "struggling"}
                onClick={() => handleAlertCardClick("struggling")}
                testId="card-struggling"
                description={showHints && thresholds ? `${thresholds.strugglingNowThreshold}+ high-severity in ${thresholds.strugglingNowHours}h` : undefined}
              />
              <AlertSummaryCard
                title="Worsening"
                count={summary.worsening}
                icon={TrendingDown}
                color="text-rose-500"
                isActive={alertType === "worsening"}
                onClick={() => handleAlertCardClick("worsening")}
                testId="card-worsening"
                description={showHints && thresholds ? `Rate increased >${Math.round(thresholds.trend3dThreshold * 100)}% vs. prior 3 days` : undefined}
              />
              <AlertSummaryCard
                title="Streaks"
                count={summary.streak}
                icon={Flame}
                color="text-orange-500"
                isActive={alertType === "streak"}
                onClick={() => handleAlertCardClick("streak")}
                testId="card-streak"
                description={showHints && thresholds ? `${thresholds.streakThresholdDays}+ consecutive days with issues` : undefined}
              />
              <AlertSummaryCard
                title="High Severity"
                count={summary.highSeverity}
                icon={AlertCircle}
                color="text-red-600"
                isActive={alertType === "highSeverity"}
                onClick={() => handleAlertCardClick("highSeverity")}
                testId="card-high-severity"
                description={showHints && thresholds ? `${thresholds.recentHighSeverityFlagThreshold}+ events in ${thresholds.recentHighSeverityDays} days` : undefined}
              />
            </div>

            {/* Gate R4: Flag Signal Summary Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <AlertSummaryCard
                title="Keyword Flag"
                count={summary.hasKeywordFlag ?? 0}
                icon={Tag}
                color="text-info"
                isActive={alertType === "hasKeywordFlag"}
                onClick={() => handleAlertCardClick("hasKeywordFlag")}
                testId="card-keyword-flag"
                description={showHints ? "Animals with a keyword-flagged interaction." : undefined}
              />
              <AlertSummaryCard
                title="Long Note Flag"
                count={summary.hasLongNoteFlag ?? 0}
                icon={FileText}
                color="text-info"
                isActive={alertType === "hasLongNoteFlag"}
                onClick={() => handleAlertCardClick("hasLongNoteFlag")}
                testId="card-long-note-flag"
                description={showHints ? "Animals with an unusually long interaction note." : undefined}
              />
              <AlertSummaryCard
                title="Highlighted"
                count={summary.hasHighlightFlag ?? 0}
                icon={Flag}
                color="text-highlight"
                isActive={alertType === "hasHighlightFlag"}
                onClick={() => handleAlertCardClick("hasHighlightFlag")}
                testId="card-highlight-flag"
                description={showHints ? "Animals with a manually highlighted interaction." : undefined}
              />
              <AlertSummaryCard
                title="Overdue"
                count={summary.overdueForInteraction ?? 0}
                icon={Clock}
                color="text-warning"
                isActive={alertType === "overdueForInteraction"}
                onClick={() => handleAlertCardClick("overdueForInteraction")}
                testId="card-overdue"
                description={showHints ? "Animals with no recent interactions recorded." : undefined}
              />
            </div>

            {/* Animals Table Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Animals in Scope</CardTitle>
                <CardDescription>
                  {animals.length} animal{animals.length !== 1 ? 's' : ''} 
                  {hasActiveFilters ? ' matching filters' : ' with behavior metrics'}
                  {alertsLocationFilter !== 'all' && ` at ${alertsLocationFilter}`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {animals.length === 0 ? (
                  hasActiveFilters ? (
                    <SearchEmptyState />
                  ) : (
                    <div className="text-center py-10" data-testid="empty-state">
                      <Filter className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        No behavior signals yet. Signals are automatically detected from daily interaction reports — they surface animals with patterns like worsening trends, streaks, or acute distress.
                      </p>
                    </div>
                  )
                ) : (
                  <div className="overflow-x-auto">
                    <Table className="table-zebra" data-testid="animals-in-scope-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[160px]">Animal</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Most Recent Concern</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Active Signals</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {animals.map((animal) => (
                          <AnimalRow 
                            key={animal.animalId} 
                            animal={animal} 
                            onClick={handleAlertsRowClick}
                            followedIds={followedIds}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gate I8.20: Animal detail drawer for alerts table row clicks */}
            {alertsDrawerAnimalId && (
              <AnimalDetailDrawer
                open={alertsDrawerOpen}
                onOpenChange={setAlertsDrawerOpen}
                animalId={alertsDrawerAnimalId}
                context="behavior"
              />
            )}
          </TabsContent>

          {/* Workflow Tab Content - Gate I8: Only rendered when feature flag enabled */}
          {workflowEnabled && (
            <TabsContent value="workflow" className="space-y-3 mt-4" data-testid="content-workflow">
              {/* Sticky filter + view controls bar */}
              <div className="sticky top-0 z-40 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-4 border-b bg-muted/80 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
              <Card>
                <CardContent className="p-5">
                  <CollapsibleFilterHeader
                    collapsed={filterCollapsed}
                    onToggle={() => setFilterCollapsed((c) => !c)}
                    description={workflowDescription}
                    hasActiveFilters={workflowLocationFilter !== 'all' || workflowSpeciesFilter !== 'all' || workflowEvalStatusFilter !== 'all' || workflowAdoptionLevelFilter !== 'all' || workflowAttention || workflowSummaryFilter !== null}
                    onClearFilters={() => {
                      setWorkflowLocationFilter('all');
                      setWorkflowSpeciesFilter('all');
                      setWorkflowEvalStatusFilter('all');
                      setWorkflowAdoptionLevelFilter('all');
                      setWorkflowAttention(false);
                      setWorkflowSummaryFilter(null);
                      setSelectedBucket(null);
                    }}
                    descriptionTestId="text-workflow-description"
                    clearTestId="button-clear-workflow-filters"
                  >
                  <div className="flex flex-wrap gap-4" data-testid="workflow-filters">
                        {/* Gate I8.11: Workflow Search - moved to left */}
                        {workflowData && (
                          <WorkflowSearch
                            items={filteredWorkflowItems}
                            viewMode={workflowViewMode}
                            onJumpToAnimal={handleSearchJumpToAnimal}
                            onOpenDetails={handleSearchOpenDetails}
                          />
                        )}
                        <Select 
                          value={workflowLocationFilter} 
                          onValueChange={(value) => {
                            setWorkflowLocationFilter(value);
                            setSelectedBucket(null);
                            // Gate I8.18: Sync workflow location filter → sub-nav (only when on Workflow tab)
                            // "all" → "system", location codes stay uppercase to match nav expectations
                            if (activeTab === 'workflow') {
                              const mappedShelter = value === 'all' ? 'system' : value;
                              setSelectedShelter(mappedShelter);
                            }
                          }}
                        >
                          <SelectTrigger className="w-auto min-w-[120px]" data-testid="select-workflow-location">
                            <MapPin className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                            <SelectValue placeholder="Location" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Locations</SelectItem>
                            <SelectItem value="SALEM">Salem</SelectItem>
                            <SelectItem value="BOSTON">Boston</SelectItem>
                            <SelectItem value="METHUEN">Methuen</SelectItem>
                            <SelectItem value="CAPE">Cape</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Select 
                          value={workflowSpeciesFilter} 
                          onValueChange={(value) => {
                            setWorkflowSpeciesFilter(value);
                            setSelectedBucket(null);
                          }}
                        >
                          <SelectTrigger className="w-auto min-w-[100px]" data-testid="select-workflow-species">
                            <PawPrint className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                            <SelectValue placeholder="Species" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Species</SelectItem>
                            <SelectItem value="DOG">Dogs</SelectItem>
                            <SelectItem value="CAT">Cats</SelectItem>
                          </SelectContent>
                        </Select>

                        <Select 
                          value={workflowEvalStatusFilter} 
                          onValueChange={(value) => {
                            setWorkflowEvalStatusFilter(value);
                            setSelectedBucket(null);
                          }}
                        >
                          <SelectTrigger className="w-auto min-w-[140px]" data-testid="select-workflow-eval-status">
                            <ClipboardCheck className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                            <SelectValue placeholder="Evaluation" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Evaluations</SelectItem>
                            <SelectItem value="INCOMPLETE">Incomplete</SelectItem>
                            <SelectItem value="NEEDS_REVIEW">Needs Review</SelectItem>
                            <SelectItem value="NEEDS_RECS">Needs Recs</SelectItem>
                            <SelectItem value="COMPLETE">Complete</SelectItem>
                          </SelectContent>
                        </Select>

                        <Select 
                          value={workflowAdoptionLevelFilter} 
                          onValueChange={(value) => {
                            setWorkflowAdoptionLevelFilter(value);
                            setSelectedBucket(null);
                          }}
                        >
                          <SelectTrigger className="w-auto min-w-[140px]" data-testid="select-workflow-adoption-level">
                            <Activity className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                            <SelectValue placeholder="Adoption Level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Levels</SelectItem>
                            <SelectItem value="L1">Level 1</SelectItem>
                            <SelectItem value="L2">Level 2</SelectItem>
                            <SelectItem value="L3">Level 3</SelectItem>
                            <SelectItem value="L4">Level 4</SelectItem>
                            <SelectItem value="missing">No Level</SelectItem>
                          </SelectContent>
                        </Select>

                    {/* Needs Attention toggle with tooltip */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={workflowAttention ? "default" : "outline"}
                          size="sm"
                          onClick={() => setWorkflowAttention(!workflowAttention)}
                          className="gap-1"
                          data-testid="workflow-toggle-attention"
                        >
                          <AlertCircle className="h-4 w-4" />
                          Needs Attention
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[240px]">
                        <p className="text-xs">Shows Available track animals with no interaction in the last 10 days</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  </CollapsibleFilterHeader>
                </CardContent>
              </Card>
              </div>

              {/* Results row + Content Card grouped to eliminate space-y-6 gap */}
              <div className="flex flex-col gap-3">
              {/* Gate S9-12: Eval Checklist Overview card */}
              {!workflowLoading && !workflowError && workflowData && workflowData.items.length > 0 && (
                <Card data-testid="card-workflow-eval-summary">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium">Eval Checklist Snapshot</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Checklist completion across the animals in the list below, based on your current filters. Click a tile to filter by status.
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {workflowSummaryFilter !== null && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 shrink-0"
                            onClick={() => { setWorkflowSummaryFilter(null); setSelectedBucket(null); }}
                            data-testid="button-clear-eval-summary-filter-card"
                          >
                            <X className="h-3 w-3" />
                            Clear
                          </Button>
                        )}
                        <ExpandCollapseButton
                          expanded={!evalSummaryCollapsed}
                          onClick={() => setEvalSummaryCollapsed(c => !c)}
                          testId="button-toggle-eval-summary"
                        />
                      </div>
                    </div>
                    {!evalSummaryCollapsed && (
                      <HintBlock show={showHints} testId="hint-eval-checklist-overview">
                        These numbers reflect the animals shown below. "Created" means an evaluation checklist has been started for that animal — "Not Started," "Complete," "Stuck 14+ days," and "Open 30+ days" are all breakdowns within that group. "No Checklist" means nothing has been started yet. Click "All" to see every animal with an active checklist, or click any other tile to filter the list to that group; click again to clear.
                      </HintBlock>
                    )}
                    {!evalSummaryCollapsed && (
                      <WorkflowEvalSummary
                        items={baseWorkflowItems}
                        activeFilter={workflowSummaryFilter}
                        onFilterChange={(f) => {
                          setWorkflowSummaryFilter(f);
                          setSelectedBucket(null);
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              )}

              {/* RESULTS section - single inline row matching /animals */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {/* View toggle */}
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-sm text-muted-foreground">View:</span>
                  <div className="inline-flex rounded-md border border-input bg-background">
                    <Button
                      variant={workflowViewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setWorkflowViewMode('grid')}
                      className="rounded-r-none"
                      data-testid="workflow-view-toggle-grid"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={workflowViewMode === 'board' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        setWorkflowViewMode('board');
                        setSelectedBucket(null);
                      }}
                      className="rounded-none border-x"
                      data-testid="workflow-view-toggle-board"
                    >
                      <Kanban className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={workflowViewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        setWorkflowViewMode('list');
                        setSelectedBucket(null);
                        setWorkflowPage(1);
                      }}
                      className="rounded-l-none"
                      data-testid="workflow-view-toggle-list"
                    >
                      <List className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Sort dropdown */}
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-sm font-medium text-muted-foreground shrink-0">Sort by</span>
                  <Select
                    value={workflowSort}
                    onValueChange={(value) => setWorkflowSort(value as 'overdue' | 'name')}
                  >
                    <SelectTrigger className="w-[180px]" data-testid="workflow-sort">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="overdue">Length of stay</SelectItem>
                      <SelectItem value="name">Name (A→Z)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Right-side controls pushed to end */}
                <div className="flex items-center gap-2 ml-auto">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => refetchWorkflow()}
                        disabled={workflowLoading}
                        aria-label="Refresh"
                        data-testid="button-refresh-workflow"
                      >
                        {workflowLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Refresh</TooltipContent>
                  </Tooltip>
                  <ReportActionItems
                    currentLocation={workflowLocationFilter}
                    defaultReportType="all_pets"
                    showOtherType={true}
                  />
                </div>
              </div>

              {/* Gate S9: active summary-filter chip */}
              {workflowSummaryFilter !== null && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">Filtered by:</span>
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1 text-sm no-default-active-elevate"
                    data-testid="badge-workflow-summary-filter"
                  >
                    {workflowSummaryFilterLabel(workflowSummaryFilter)}
                    <button
                      type="button"
                      aria-label="Clear summary filter"
                      onClick={() => setWorkflowSummaryFilter(null)}
                      className="ml-1 rounded-sm opacity-70 hover:opacity-100"
                      data-testid="button-clear-workflow-summary-filter"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                </div>
              )}

              {/* Content Card */}
              <Card>
                <CardContent className="p-4">
                  {/* Loading State */}
                  {workflowLoading && (
                    <div className="flex flex-col items-center justify-center py-16">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
                      <p className="text-muted-foreground text-sm">Loading workflow data...</p>
                    </div>
                  )}
                  
                  {/* Error State */}
                  {workflowError && !workflowLoading && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <AlertCircle className="h-10 w-10 text-destructive mb-4" />
                      <p className="text-destructive font-medium mb-2">
                        Unable to load workflow data
                      </p>
                      <p className="text-muted-foreground text-sm mb-4">
                        {workflowError instanceof Error ? workflowError.message : 'An error occurred'}
                      </p>
                      <Button variant="outline" onClick={() => refetchWorkflow()}>
                        Try again
                      </Button>
                    </div>
                  )}
                  
                  {/* Empty State - no data from API */}
                  {!workflowLoading && !workflowError && workflowData && workflowData.items.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <LayoutGrid className="h-10 w-10 text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground">
                        No animals match the current filters.
                      </p>
                    </div>
                  )}
                  
                  {/* Empty State - filtered to zero (filter-aware) */}
                  {!workflowLoading && !workflowError && workflowData && workflowData.items.length > 0 && filteredWorkflowItems.length === 0 && (() => {
                    // Priority: tile filter > attention > dropdown filters > fallback
                    if (workflowSummaryFilter) {
                      const label = workflowSummaryFilterLabel(workflowSummaryFilter);
                      return (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                          <SlidersHorizontal className="h-10 w-10 text-muted-foreground/50 mb-4" />
                          <p className="text-muted-foreground">
                            No animals match &ldquo;{label}&rdquo; with your current filters.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-4"
                            onClick={() => setWorkflowSummaryFilter(null)}
                          >
                            Clear tile filter
                          </Button>
                        </div>
                      );
                    }
                    if (workflowAttention) {
                      return (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                          <Bell className="h-10 w-10 text-muted-foreground/50 mb-4" />
                          <p className="text-muted-foreground">
                            No animals need attention with current filter settings.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-4"
                            onClick={() => {
                              setWorkflowAttention(false);
                              setWorkflowSort('overdue');
                            }}
                          >
                            Clear attention filter
                          </Button>
                        </div>
                      );
                    }
                    if (workflowEvalStatusFilter !== 'all' || workflowAdoptionLevelFilter !== 'all') {
                      return (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                          <SlidersHorizontal className="h-10 w-10 text-muted-foreground/50 mb-4" />
                          <p className="text-muted-foreground">
                            No animals match the current filters.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-4"
                            onClick={() => {
                              setWorkflowEvalStatusFilter('all');
                              setWorkflowAdoptionLevelFilter('all');
                            }}
                          >
                            Reset filters
                          </Button>
                        </div>
                      );
                    }
                    return (
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <SlidersHorizontal className="h-10 w-10 text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">
                          No animals match the current filters.
                        </p>
                      </div>
                    );
                  })()}
                  

                  {/* Grid or Board View */}
                  {!workflowLoading && !workflowError && workflowData && filteredWorkflowItems.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                        <span>
                          <span className="text-sm font-medium uppercase tracking-wider mr-1">Showing</span>
                          <span className="text-sm text-muted-foreground">—</span>
                          <span className="text-sm text-foreground ml-1">{filteredWorkflowItems.length} {filteredWorkflowItems.length === 1 ? 'animal' : 'animals'}</span>
                          {(workflowAttention || workflowSort !== 'overdue') && filteredWorkflowItems.length !== workflowData.meta.totalCount && (
                            <span> (filtered from {workflowData.meta.totalCount})</span>
                          )}
                          {!workflowAttention && workflowData.meta.missingDataCount > 0 && workflowData.meta.excludedAnimals && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <button 
                                  className="ml-2 text-muted-foreground hover:text-foreground hover:underline cursor-pointer"
                                  data-testid="button-excluded-animals"
                                >
                                  ({workflowData.meta.missingDataCount} excluded due to missing data)
                                </button>
                              </DialogTrigger>
                              <DialogContent className="max-w-md">
                                <DialogHeader>
                                  <DialogTitle>Excluded Animals</DialogTitle>
                                  <DialogDescription>
                                    These animals are missing required data (track or evaluation status).
                                  </DialogDescription>
                                </DialogHeader>
                                <ScrollArea className="max-h-[300px] pr-4">
                                  <div className="space-y-1">
                                    {workflowData.meta.excludedAnimals.map((animal) => (
                                      <div 
                                        key={animal.animalId} 
                                        className="flex items-center justify-between text-sm py-2 border-b border-border/50 last:border-0"
                                      >
                                        <span className="font-mono text-xs">{animal.animalId}</span>
                                        <div className="flex items-center gap-2">
                                          <span className="text-muted-foreground truncate max-w-[120px]">
                                            {animal.name || 'Unknown name'}
                                          </span>
                                          <Link href={`/animals/${animal.animalId}`}>
                                            <Button 
                                              variant="ghost" 
                                              size="icon" 
                                              className="h-6 w-6"
                                              data-testid={`button-view-excluded-${animal.animalId}`}
                                            >
                                              <Eye className="h-3.5 w-3.5" />
                                            </Button>
                                          </Link>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </ScrollArea>
                              </DialogContent>
                            </Dialog>
                          )}
                        </span>
                        <span className="text-xs">
                          Ruleset v{workflowData.meta.rulesetVersion}
                        </span>
                      </div>
                      
                      <div data-testid="workflow-animal-list">
                        {workflowViewMode === 'grid' && (
                          <>
                            {/* When no bucket selected: show full grid */}
                            {!selectedBucket && (
                              <WorkflowDashboardGrid
                                bucketCounts={filteredBucketCounts}
                                selectedBucket={selectedBucket}
                                onBucketSelect={(bucketKey) => {
                                  setSelectedBucket(bucketKey);
                                }}
                              />
                            )}
                            
                            {/* When bucket selected: show collapsed indicator with back button + results */}
                            {selectedBucket && workflowData && (
                              <div className="space-y-4">
                                {/* Selected bucket indicator bar */}
                                <div className="flex flex-wrap items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedBucket(null)}
                                    className="gap-1 shrink-0"
                                    data-testid="button-clear-bucket-selection"
                                  >
                                    <ArrowLeft className="h-4 w-4" />
                                    Back to Grid
                                  </Button>
                                  <div className="hidden sm:block h-5 w-px bg-border shrink-0" />
                                  <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                                    <span className="text-sm font-medium shrink-0">Viewing:</span>
                                    <Badge variant="default" className="text-xs shrink-0 max-w-[200px] truncate">
                                      {TRACK_LABELS[selectedBucket.split(':')[0] as Track]} / {EVAL_STATUS_LABELS[selectedBucket.split(':')[1] as EvalStatus]}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground shrink-0">
                                      ({selectedBucketItems.length} {selectedBucketItems.length === 1 ? 'animal' : 'animals'})
                                    </span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSelectedBucket(null)}
                                    className="gap-1 ml-auto shrink-0"
                                    data-testid="button-show-all-buckets"
                                  >
                                    <LayoutGrid className="h-4 w-4" />
                                    <span className="hidden sm:inline">Show All Animals</span>
                                    <span className="sm:hidden">All Animals</span>
                                  </Button>
                                </div>
                                
                                {/* Bucket detail results (inline, not below) */}
                                <WorkflowBucketDetail
                                  bucketKey={selectedBucket}
                                  items={selectedBucketItems}
                                  highlightedAnimalId={highlightedAnimalId}
                                />
                              </div>
                            )}
                          </>
                        )}
                        {workflowViewMode === 'board' && (
                          <WorkflowBoardView 
                            items={filteredWorkflowItems}
                            highlightedAnimalId={highlightedAnimalId}
                          />
                        )}
                        {workflowViewMode === 'list' && (
                          <>
                            {/* Gate I8.15.1: List view table with sortable columns */}
                            <div className="rounded-md border overflow-x-auto" data-testid="workflow-list-table">
                              <table className="w-full min-w-[800px] table-zebra">
                                <thead className="bg-muted/50">
                                  <tr>
                                    <th 
                                      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:bg-muted/70 select-none w-[160px]"
                                      onClick={() => handleListColumnSort('animal')}
                                      data-testid="workflow-list-header-animal"
                                    >
                                      <div className="flex items-center gap-1">
                                        Animal
                                        {listSortColumn === 'animal' && (
                                          listSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                                        )}
                                      </div>
                                    </th>
                                    <th 
                                      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:bg-muted/70 select-none"
                                      onClick={() => handleListColumnSort('track')}
                                      data-testid="workflow-list-header-track"
                                    >
                                      <div className="flex items-center gap-1">
                                        Status
                                        {listSortColumn === 'track' && (
                                          listSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                                        )}
                                      </div>
                                    </th>
                                    <th 
                                      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:bg-muted/70 select-none"
                                      onClick={() => handleListColumnSort('evalStatus')}
                                      data-testid="workflow-list-header-evalStatus"
                                    >
                                      <div className="flex items-center gap-1">
                                        Evaluation Status
                                        {listSortColumn === 'evalStatus' && (
                                          listSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                                        )}
                                      </div>
                                    </th>
                                    <th 
                                      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:bg-muted/70 select-none"
                                      onClick={() => handleListColumnSort('adoptionLevel')}
                                      data-testid="workflow-list-header-adoptionLevel"
                                    >
                                      <div className="flex items-center gap-1">
                                        Adoption Level
                                        {listSortColumn === 'adoptionLevel' && (
                                          listSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                                        )}
                                      </div>
                                    </th>
                                    <th 
                                      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:bg-muted/70 select-none"
                                      onClick={() => handleListColumnSort('location')}
                                      data-testid="workflow-list-header-location"
                                    >
                                      <div className="flex items-center gap-1">
                                        Location / Kennel
                                        {listSortColumn === 'location' && (
                                          listSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                                        )}
                                      </div>
                                    </th>
                                    <th 
                                      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:bg-muted/70 select-none"
                                      onClick={() => handleListColumnSort('intakeDate')}
                                      data-testid="workflow-list-header-intakeDate"
                                    >
                                      <div className="flex items-center gap-1">
                                        Intake Date
                                        {listSortColumn === 'intakeDate' && (
                                          listSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                                        )}
                                      </div>
                                    </th>
                                    <th 
                                      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:bg-muted/70 select-none"
                                      onClick={() => handleListColumnSort('onHold')}
                                      data-testid="workflow-list-header-onHold"
                                    >
                                      <div className="flex items-center gap-1">
                                        On Hold
                                        {listSortColumn === 'onHold' && (
                                          listSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                                        )}
                                      </div>
                                    </th>
                                    <th 
                                      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:bg-muted/70 select-none"
                                      onClick={() => handleListColumnSort('attention')}
                                      data-testid="workflow-list-header-attention"
                                    >
                                      <div className="flex items-center gap-1">
                                        Needs Attention
                                        {listSortColumn === 'attention' && (
                                          listSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                                        )}
                                      </div>
                                    </th>
                                    <th 
                                      className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:bg-muted/70 select-none"
                                      onClick={() => handleListColumnSort('evalChecklist')}
                                      data-testid="workflow-list-header-evalChecklist"
                                    >
                                      <div className="flex items-center gap-1">
                                        Eval Checklist
                                        {listSortColumn === 'evalChecklist' && (
                                          listSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                                        )}
                                      </div>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {listViewPagination.items.length > 0 ? (
                                    listViewPagination.items.map((item) => (
                                      <tr 
                                        key={item.animalId}
                                        id={`workflow-list-animal-${item.animalId}`}
                                        className={`transition-colors cursor-pointer ${highlightedAnimalId === item.animalId ? 'ring-2 ring-primary ring-inset bg-primary/5 animate-pulse' : 'hover:bg-muted/50'}`}
                                        onClick={() => handleListRowClick(item)}
                                        data-testid={`workflow-list-row-${item.animalId}`}
                                      >
                                        <td className="px-4 py-3">
                                          <AnimalCell
                                            animalId={item.animalId}
                                            name={item.name || null}
                                            species={item.species}
                                            photoUrl={item.photoUrl ?? null}
                                            speciesTestId={`badge-species-${item.animalId}`}
                                            inlineBadges={
                                              followedIds.includes(item.animalId) ? (
                                                <Heart className="h-3 w-3 fill-rose-500 text-rose-500 shrink-0" />
                                              ) : undefined
                                            }
                                          />
                                        </td>
                                        <td className="px-4 py-3">
                                          <Badge variant="outline" className="text-xs">
                                            {TRACK_LABELS[item.track] || item.track}
                                          </Badge>
                                        </td>
                                        <td className="px-4 py-3">
                                          <EvalStatusBadge
                                            status={item.evalStatus}
                                            className="text-xs"
                                          />
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className="text-sm">{item.adoptionLevel || '—'}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                          <div className="text-sm">
                                            {item.location || '—'}
                                            {item.zoneCode && (
                                              <span className="text-muted-foreground"> / {resolveLabel(item.zoneCode)}</span>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className="text-sm">
                                            {item.intakeDate 
                                              ? format(new Date(item.intakeDate), "MMM d, yyyy")
                                              : '—'}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3">
                                          {item.isOnHold ? (
                                            <Badge variant="secondary" className="text-xs">Yes</Badge>
                                          ) : (
                                            <span className="text-muted-foreground text-sm">No</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3">
                                          {item.hasAttentionFlag ? (
                                            <Badge variant="destructive" className="text-xs">Yes</Badge>
                                          ) : (
                                            <span className="text-muted-foreground text-sm">No</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3" data-testid={`workflow-list-evalchecklist-${item.animalId}`}>
                                          {item.evalActivated ? (
                                            <span className="text-sm flex items-center gap-1">
                                              {item.evalDone}/{item.evalTotal}
                                              {item.evalTotal > 0 && item.evalDone === item.evalTotal && (
                                                <CheckCheck className="h-3 w-3 text-muted-foreground" />
                                              )}
                                            </span>
                                          ) : (
                                            <span className="text-muted-foreground text-sm">—</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                                        No animals match the current filters.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>

                            {/* Gate I8.15: List view pagination using shared component */}
                            {listViewPagination.totalPages > 0 && (
                              <PaginationControls
                                currentPage={listViewPagination.currentPage}
                                totalPages={listViewPagination.totalPages}
                                total={listViewPagination.total}
                                pageSize={workflowPageSize}
                                startIndex={listViewPagination.startIndex}
                                endIndex={listViewPagination.endIndex}
                                onPageChange={setWorkflowPage}
                                onPageSizeChange={(size) => {
                                  setWorkflowPageSize(size);
                                  setWorkflowPage(1);
                                }}
                                itemLabel="animals"
                                testIdPrefix="workflow"
                                className="border rounded-md mt-4"
                              />
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              </div>

              {/* Gate I8.11: Animal detail drawer for search results */}
              {searchDrawerAnimalId && (
                <AnimalDetailDrawer
                  open={searchDrawerOpen}
                  onOpenChange={setSearchDrawerOpen}
                  animalId={searchDrawerAnimalId}
                  context="behavior"
                />
              )}

              {/* Gate I8.15: Animal detail drawer for list row clicks */}
              {listDrawerAnimalId && (
                <AnimalDetailDrawer
                  open={listDrawerOpen}
                  onOpenChange={setListDrawerOpen}
                  animalId={listDrawerAnimalId}
                  context="behavior"
                />
              )}
            </TabsContent>
          )}

          {/* Gate I16 Phase 4: Analytics Tab Content */}
          <TabsContent value="analytics" className="mt-4">
            <BehaviorAnalytics 
              selectedLocation={selectedShelter} 
              isSystemView={isSystemView}
              staleLocationCount={alertsStaleLocationCount}
              days={analyticsDays}
              onDaysChange={setAnalyticsDays}
              showHints={showHints}
            />
          </TabsContent>
        </Tabs>
      </div>
    </InsightsPageWrapper>
  );
}
